<script setup>
import { ref, nextTick, watch, onMounted, onUnmounted, Teleport } from 'vue'
import BcsTooltip from '../BcsTooltip.vue'

const props = defineProps({
  /** Unique ID for this dropdown instance (e.g. row.id) */
  itemId: { type: [String, Number], required: true },
  /** Array of { key, label, disabled?, tooltipText?, variant? } action objects */
  actions: { type: Array, required: true },
  /**
   * 'teleport' — renders dropdown via Teleport to body with fixed positioning (for tables with overflow)
   * 'inline' — renders dropdown inline with absolute positioning (simpler use cases)
   */
  mode: { type: String, default: 'inline', validator: v => ['inline', 'teleport'].includes(v) },
  /** An action of this row is in flight: the trigger swaps the dots for a spinner and ignores clicks. */
  loading: { type: Boolean, default: false },
  /** Blocks the trigger — e.g. while another row's action is in flight. */
  disabled: { type: Boolean, default: false },
  /** Accessible name of the icon-only trigger; tables should name the row, e.g. "Actions for Jane Doe". */
  ariaLabel: { type: String, default: 'More actions' }
})

const emits = defineEmits(['action-click'])

const isOpen = ref(false)
const btnRef = ref(null)
const dropdownRef = ref(null)
const dropdownPosition = ref({ top: 0, left: 0 })

function isBlocked() {
  return props.disabled || props.loading
}

function toggle() {
  if (isBlocked()) return
  if (isOpen.value) {
    close()
    return
  }
  isOpen.value = true
  if (props.mode === 'teleport') {
    nextTick(() => updatePosition())
  }
}

function close() {
  isOpen.value = false
}

// A menu left open when the trigger becomes blocked would still accept clicks.
watch(isBlocked, (blocked) => {
  if (blocked) close()
})

function onActionClick(actionKey) {
  close()
  emits('action-click', actionKey)
}

function updatePosition() {
  if (!btnRef.value) return
  const rect = btnRef.value.getBoundingClientRect()
  dropdownPosition.value = {
    top: rect.bottom + 2,
    left: rect.right
  }
}

function handleClickOutside(event) {
  if (!isOpen.value) return
  if (btnRef.value && btnRef.value.contains(event.target)) return
  if (dropdownRef.value && dropdownRef.value.contains(event.target)) return
  close()
}

function handleScroll() {
  if (isOpen.value && props.mode === 'teleport') {
    updatePosition()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside, true)
  if (props.mode === 'teleport') {
    window.addEventListener('scroll', handleScroll, true)
  }
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside, true)
  if (props.mode === 'teleport') {
    window.removeEventListener('scroll', handleScroll, true)
  }
})

defineExpose({ close })
</script>

<template>
  <div class="bcs-dropdown-actions-wrapper" :class="{ 'position-relative': mode === 'inline' }">
    <!-- `active` while open: Bootstrap fills the outline button, which is how the design marks
         the row whose menu is showing. -->
    <button
      ref="btnRef"
      class="btn btn-outline-primary btn-sm bcs-dropdown-trigger"
      :class="{ active: isOpen, 'bcs-dropdown-trigger--blocked': disabled || loading }"
      type="button"
      :disabled="disabled || loading"
      :aria-label="ariaLabel"
      :aria-expanded="isOpen"
      :aria-busy="loading"
      @click.stop="toggle"
    >
      <span v-if="loading" class="spinner-border bcs-dropdown-trigger__spinner" role="status">
        <span class="visually-hidden">Loading...</span>
      </span>
      <i v-else class="bi bi-three-dots"></i>
    </button>

    <!-- Inline dropdown -->
    <div
      v-if="mode === 'inline' && isOpen"
      ref="dropdownRef"
      class="bcs-dropdown-menu bcs-dropdown-menu--inline"
    >
      <template v-for="action in actions" :key="action.key">
        <BcsTooltip
          v-if="action.disabled && action.tooltipText"
          :simpleText="action.tooltipText"
          placement="left"
        >
          <button
            class="dropdown-item disabled"
            disabled
          >
            {{ action.label }}
          </button>
        </BcsTooltip>
        <button
          v-else
          class="dropdown-item"
          :class="{ [`dropdown-item--${action.variant}`]: action.variant }"
          :disabled="action.disabled"
          @click="onActionClick(action.key)"
        >
          {{ action.label }}
        </button>
      </template>
    </div>

    <!-- Teleported dropdown -->
    <Teleport to="body">
      <div
        v-if="mode === 'teleport' && isOpen"
        ref="dropdownRef"
        class="bcs-dropdown-menu bcs-dropdown-menu--teleport"
        :style="{
          position: 'fixed',
          top: dropdownPosition.top + 'px',
          left: dropdownPosition.left + 'px',
          transform: 'translateX(-100%)'
        }"
      >
        <template v-for="action in actions" :key="action.key">
          <BcsTooltip
            v-if="action.disabled && action.tooltipText"
            :simpleText="action.tooltipText"
            placement="left"
          >
            <button
              class="dropdown-item disabled"
              disabled
            >
              {{ action.label }}
            </button>
          </BcsTooltip>
          <button
            v-else
            class="dropdown-item"
            :class="{ [`dropdown-item--${action.variant}`]: action.variant }"
            :disabled="action.disabled"
            @click="onActionClick(action.key)"
          >
            {{ action.label }}
          </button>
        </template>
      </div>
    </Teleport>
  </div>
</template>

<style scoped lang="scss">
.bcs-dropdown-trigger {
  padding: 6px 8px;
  line-height: 1;
  border-radius: 4px;
}

/* Same box as the dots glyph, so swapping one for the other never resizes the button. */
.bcs-dropdown-trigger__spinner {
  width: 14px;
  height: 14px;
  border-width: 2px;
  vertical-align: middle;
}

/*
 * Blocked or in-flight trigger — same convention as the Transform Tracker's View Progress
 * button: solid gray with white content and the not-allowed cursor.
 * Bootstrap kills pointer events on a disabled .btn, which also kills the cursor; restoring them
 * lets the cursor show while the disabled attribute still blocks the click. Every interactive
 * state is pinned so the outline button never lights up under the pointer while inert.
 */
.bcs-dropdown-trigger.bcs-dropdown-trigger--blocked:disabled,
.bcs-dropdown-trigger.bcs-dropdown-trigger--blocked:disabled:hover,
.bcs-dropdown-trigger.bcs-dropdown-trigger--blocked:disabled:focus,
.bcs-dropdown-trigger.bcs-dropdown-trigger--blocked:disabled:active {
  pointer-events: auto;
  cursor: not-allowed;
  background: #6c757d;
  border-color: #6c757d;
  color: #fff;
  opacity: 0.65;
  box-shadow: none;
}
</style>

<!-- Global styles (teleported dropdown is outside scoped context) -->
<style lang="scss">
.bcs-dropdown-menu {
  z-index: 1050;
  background: #fff;
  border: 1px solid #1e2125;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  min-width: 180px;
  padding: 0.25rem 0;

  /* Text - 16 - Medium */
  .dropdown-item {
    display: block;
    width: 100%;
    padding: 0.5rem 1rem;
    border: none;
    background: none;
    text-align: left;
    font-family: "DM Sans", sans-serif;
    font-size: 16px;
    font-weight: 500;
    line-height: 130%;
    letter-spacing: -0.32px;
    color: #5c0cb8;
    cursor: pointer;

    &:hover:not(:disabled) {
      background-color: var(--bs-primary);
      color: #fff;
    }

    &.disabled,
    &:disabled {
      color: var(--bs-gray-500);
      cursor: not-allowed;
      opacity: 0.65;
    }

    &--danger {
      color: var(--bs-danger);

      &:hover:not(:disabled) {
        background-color: var(--bs-danger);
        color: #fff;
      }
    }
  }
}

.bcs-dropdown-menu .tooltip-trigger {
  display: block;
  width: 100%;
}

.bcs-dropdown-menu--inline {
  position: absolute;
  right: 0;
  top: 100%;
}
</style>
